import { create } from 'zustand';
import type { AIToolType } from '@/types/common';
import { useAuthStore } from '@/store/authStore';

interface UIState {
  sidebarOpen: boolean;
  aiPanelOpen: boolean;
  activeAITool: AIToolType;
  aiContext: {
    page?: string;
    resourceId?: string;
    resourceData?: unknown;
  } | null;
  mobileMenuOpen: boolean;
  mobileMoreSheetOpen: boolean;
  aiSlideOpen: boolean;
  currentRoute: string;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  toggleAIPanel: () => void;
  setAIPanelOpen: (open: boolean) => void;
  setActiveAITool: (tool: AIToolType) => void;
  setAIContext: (context: UIState['aiContext']) => void;
  setMobileMenuOpen: (open: boolean) => void;
  setMobileMoreSheetOpen: (open: boolean) => void;
  setAISlideOpen: (open: boolean) => void;
  toggleAISlide: () => void;
  setCurrentRoute: (route: string) => void;
  reset: () => void;
}

// Ephemeral, session-scoped UI state. `aiContext.resourceData` can hold a full
// deal or contract object (set from DealDetail.tsx / ContractAnalyzer.tsx), so
// it must never outlive the session that produced it. Keeping the defaults in
// one place lets reset() restore them without drifting from the initializer.
const initialUIState: Pick<
  UIState,
  | 'sidebarOpen'
  | 'aiPanelOpen'
  | 'activeAITool'
  | 'aiContext'
  | 'mobileMenuOpen'
  | 'mobileMoreSheetOpen'
  | 'aiSlideOpen'
  | 'currentRoute'
> = {
  sidebarOpen: true,
  aiPanelOpen: false,
  activeAITool: 'general_chat',
  aiContext: null,
  mobileMenuOpen: false,
  mobileMoreSheetOpen: false,
  aiSlideOpen: false,
  currentRoute: '/',
};

export const useUIStore = create<UIState>((set) => ({
  ...initialUIState,
  toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
  setSidebarOpen: (sidebarOpen) => set({ sidebarOpen }),
  toggleAIPanel: () => set((state) => ({ aiPanelOpen: !state.aiPanelOpen })),
  setAIPanelOpen: (aiPanelOpen) => set({ aiPanelOpen }),
  setActiveAITool: (activeAITool) => set({ activeAITool }),
  setAIContext: (aiContext) => set({ aiContext }),
  setMobileMenuOpen: (mobileMenuOpen) => set({ mobileMenuOpen }),
  setMobileMoreSheetOpen: (mobileMoreSheetOpen) => set({ mobileMoreSheetOpen }),
  setAISlideOpen: (aiSlideOpen) => set({ aiSlideOpen }),
  toggleAISlide: () => set((state) => ({ aiSlideOpen: !state.aiSlideOpen })),
  setCurrentRoute: (currentRoute) => set({ currentRoute }),
  reset: () => set({ ...initialUIState }),
}));

// Cross-store teardown for FESTATE-065-M-001.
//
// The cross-user leak requires a logout (or account switch) on the same SPA
// instance: Creator A's deal/contract data sits in `aiContext.resourceData`,
// Creator A signs out, Creator B signs in, and B could otherwise read A's data
// from the still-populated AI context. We tie the wipe to the authenticated
// identity rather than to navigation because the auth transition is the path
// that actually executes on every sign-out/sign-in: useAuth.signOut() calls
// useAuthStore.reset() (profile -> null) and signIn()/signUp() call
// setProfile() (profile -> new id). A route-change hook, by contrast, lives in
// the router/app shell (outside this store) and is not wired today, so it
// would never fire. Comparing by `profile.id` ensures a same-user profile
// refresh (same id, new object) does NOT discard the live AI context.
useAuthStore.subscribe((state, prevState) => {
  const currentId = state.profile?.id ?? null;
  const previousId = prevState.profile?.id ?? null;
  if (currentId !== previousId) {
    useUIStore.getState().reset();
  }
});
